import { useCallback, useRef, useState, type RefObject } from "react";
import { usePermissions } from "react-admin";
import type { PermissionRecord } from "@/types/permissions";
import { hasAnyPermission } from "@/utils/permission-utils";

export type UseCsvImportDialogStateResult = {
  open: boolean;
  setOpen: (open: boolean) => void;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  openDialog: () => void;
  closeDialog: () => void;
  chooseFile: () => void;
};

/**
 * Shared dialog shell state for CSV import buttons (open / busy / file input).
 */
export function useCsvImportDialogState(): UseCsvImportDialogStateResult {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const openDialog = useCallback(() => setOpen(true), []);
  const closeDialog = useCallback(() => setOpen(false), []);
  const chooseFile = useCallback(() => inputRef.current?.click(), []);

  return {
    open,
    setOpen,
    busy,
    setBusy,
    inputRef,
    openDialog,
    closeDialog,
    chooseFile,
  };
}

/** Permission gate for CSV import entry points. */
export function useCanCsvImport(requiredPermissions: string[]): boolean {
  const { permissions } = usePermissions<PermissionRecord>();
  return hasAnyPermission(permissions, requiredPermissions);
}
